/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { Tooltip, Typography, TypographyProps } from '@mui/material';
import { ElementType, ReactNode, useEffect, useRef, useState } from 'react';
import { isTextRTL } from '../../../../home/v1/utils/rtl-text';
import { isTextOverflowing } from '../../../utils/is-text-overflowing';

export type TypographyWithEllipsisTooltipProps<P extends ElementType<any>> = TypographyProps<
  P,
  {
    children?: ReactNode;
    component?: P;
    showArrow?: boolean;
  }
>;

function TypographyWithEllipsisTooltip<P extends ElementType<any> = 'span'>({
  sx,
  children,
  component,
  showArrow = false,
  ...props
}: TypographyWithEllipsisTooltipProps<P>) {
  const textRef = useRef<HTMLElement>(null);
  const [isOverflowing, setIsOverflowing] = useState<boolean>(false);

  useEffect(() => {
    if (!textRef.current) return;

    const checkOverflow = () => {
      if (textRef.current) {
        setIsOverflowing(isTextOverflowing(textRef.current));
      }
    };
    checkOverflow();
    const resizeObserver = new ResizeObserver(checkOverflow);
    if (textRef.current) {
      resizeObserver.observe(textRef.current);
    }
    return () => {
      if (textRef.current) {
        resizeObserver.unobserve(textRef.current);
      }
    };
  }, [children, textRef.current?.clientWidth, textRef.current?.scrollWidth]);

  if (!children) return null;

  let direction: string | undefined;

  if (typeof children === 'string') {
    direction = isTextRTL(children) ? 'rtl' : 'ltr';
  }

  const renderText = (text: ReactNode) => {
    return (
      <Typography
        component={component}
        sx={{
          ...sx,
        }}
        {...props}
        dir={props.dir ? props.dir : direction}
        ref={textRef}
        css={css`
          color: #2d2d2d;
          font-family: Inter;
          font-size: 14px;
          font-weight: 600;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        `}
      >
        {text}
      </Typography>
    );
  };

  if (isOverflowing) {
    return (
      <Tooltip placement="top" title={children} arrow={showArrow}>
        {renderText(children)}
      </Tooltip>
    );
  }

  return renderText(children);
}

export default TypographyWithEllipsisTooltip;
